/**
 * ThemedCheckbox — brand-styled checkbox wrapper using Radix UI.
 * @layer-1 — themed wrapper. All feature checkboxes use this component.
 *
 * All colors use CSS custom properties for dark mode support.
 * Checked: var(--foreground) fill, card checkmark.
 * Unchecked: var(--color-input-border) border, card fill.
 * Focus: foreground border + ring for visibility in both themes.
 *
 * Does NOT include a label — use Label from ui/ or feature-specific markup.
 */
import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import { cn } from "@/shared/lib/utils";

interface ThemedCheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> {
  /** Optional additional class for the indicator */
  indicatorClassName?: string;
}

const ThemedCheckbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, ThemedCheckboxProps>(
  ({ className, indicatorClassName, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "peer h-4 w-4 shrink-0 rounded-sm",
        "border bg-[var(--card)]",
        "border-[var(--color-input-border)]",
        "focus:outline-none focus:border-[var(--foreground)] focus:ring-1 focus:ring-[var(--foreground)] focus:ring-offset-1",
        "disabled:cursor-not-allowed disabled:opacity-40",
        "data-[state=checked]:bg-[var(--foreground)] data-[state=checked]:border-[var(--foreground)]",
        "data-[state=checked]:text-[var(--card)]",
        "transition-colors duration-150",
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        className={cn("flex items-center justify-center text-current", indicatorClassName)}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
);
ThemedCheckbox.displayName = CheckboxPrimitive.Root.displayName;

export { ThemedCheckbox };
